import { environmentHttpClient } from "client/Api/httpClient";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import {
	ToDoListIdentifier,
	ToDoListIdentifierKind,
	WellKnownTodoListKind
} from "./toDoListLiveInformation";

export const enum ListItemIdKind {
	Exchange = 1,
	Planner = 2
}

export type ListItemId = {
	kind: ListItemIdKind.Exchange;
	id: string;
}
	|
{
	kind: ListItemIdKind.Planner;
	id: string;
};

export type ListItemIdModel =
	{
		kind: ListItemIdKind.Exchange;
		id: string;
	}
	|
	{
		kind: ListItemIdKind.Planner;
		id: string;
	};

export type ToDoListItem = {
	id: ListItemId;
	name: string;
	dueDate?: string;
};

export type CreateTaskModel = {
	list: ToDoListIdentifierModel;
	name: string;
};

export function createToDo(list: ToDoListIdentifier, name: string): Promise<ListItemId> {
	const model: CreateTaskModel = {
		list: convertListIdentifierToModel(list),
		name
	};

	return environmentHttpClient.post<ListItemId>(
		"/Workspace/LiveInformation/ToDos",
		model
	);
}

export type MarkAsCompleteRequestModel = {
	itemId: ListItemIdModel;
	listIdentifier: ToDoListIdentifierModel;
};

export function markToDoAsComplete(model: MarkAsCompleteRequestModel): Promise<void> {
	return environmentHttpClient.put<void>(
		"/Workspace/LiveInformation/ToDos/MarkAsComplete",
		model
	);
}

export type TodoTilePreferenceSaveModel = {
	listId: ToDoListIdentifierModel;
};

export function setPreferences(
	tileId: number,
	model: TodoTilePreferenceSaveModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.put<void>(
		`/Workspace/LiveInformation/ToDos/Preferences/${tileId}`,
		model,
		{ signal }
	);
}

export type TodoTilePreferenceViewModel = {
	tileId: number;
	listId: ToDoListIdentifier;
};

export function getPreferences(
	signal?: AbortSignal
): Promise<readonly TodoTilePreferenceViewModel[]> {
	return environmentHttpClient.get<readonly TodoTilePreferenceViewModel[]>(
		"/Workspace/LiveInformation/ToDos/Preferences",
		undefined,
		{ signal }
	);
}

export type Interests = {
	items?: boolean;
};

export type DefaultToDoListIdentifierModel = {};
export type PlannerToDoListIdentifierModel = {};
export type SpecificToDoListIdentifierModel = { id: string; };
export type WellKnownToDoListIdentifierModel = {
	id: string;
	wellKnownListKind: WellKnownTodoListKind;
};

export type ToDoListIdentifierModel =
	| { default: DefaultToDoListIdentifierModel; }
	| { planner: PlannerToDoListIdentifierModel; }
	| { specific: SpecificToDoListIdentifierModel; }
	| { wellknown: WellKnownToDoListIdentifierModel; };

export type LiveInformationRequestModel = {
	list: ToDoListIdentifierModel;
	interestedIn: Interests;
};

export const enum ItemsResultStatus {
	GeneralFailure = 0,
	Success = 1,
	NoAccess = 2
}

export type ItemsResult = {
	status: ItemsResultStatus;
	items: ToDoListItem[];
};

export type LiveInformationResponseModelData = {
	items?: ItemsResult;
};

export type LiveInformationResponseModel = {
	list: ToDoListIdentifier;
	interestedIn: Interests;
	data: LiveInformationResponseModelData;
};

export function getLiveInformation(utcOffset: string, requestModels: LiveInformationRequestModel[]): Promise<LiveInformationResponseModel[]> {
	return environmentHttpClient.post<LiveInformationResponseModel[]>(
		"/Workspace/LiveInformation/ToDos/Get",
		{
			utcOffset,
			requestModels
		}
	);
}

export function convertListIdentifierToModel(identifier: ToDoListIdentifier): ToDoListIdentifierModel {
	switch (identifier.kind) {
		case ToDoListIdentifierKind.Default:
			return {
				default: {}
			};
		case ToDoListIdentifierKind.Specific:
			return {
				specific: {
					id: identifier.id
				}
			};
		case ToDoListIdentifierKind.Planner:
			return {
				planner: {}
			};
		case ToDoListIdentifierKind.WellKnown:
			return {
				wellknown: {
					id: identifier.id,
					wellKnownListKind: identifier.wellKnownListKind
				}
			};
		default:
			throw new UnreachableCaseError(identifier);
	}
}
